import { TmdbToggle } from "./TmdbSettings";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckIcon from "@material-ui/icons/Check";
import SendIcon from "@material-ui/icons/Send";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDialogStyles } from "../../theme/redesign";
import { FilterChips, tNegocio } from "./shared";
import { useStyles } from "./styles";

const t = (k) => tNegocio(`pages.ai.${k}`);

const PROVIDERS = [
  { value: "openai-oficial-gpt41-mini", label: "OpenAI — GPT-4.1 mini (recomendado)" },
  { value: "openai-oficial-gpt4o-mini", label: "OpenAI — GPT-4o mini (económico)" },
  { value: "openai-oficial-gpt41", label: "OpenAI — GPT-4.1" },
  { value: "openai-oficial-gpt4o", label: "OpenAI — GPT-4o" },
  { value: "gemini-oficial-flash-15", label: "Google Gemini — 2.5 Flash" },
  { value: "gemini-oficial-pro", label: "Google Gemini — 2.5 Pro" },
  { value: "anthropic-oficial-claude-haiku", label: "Anthropic — Claude Haiku" },
  { value: "anthropic-oficial-claude-sonnet", label: "Anthropic — Claude Sonnet" },
  { value: "openrouter-gpt4o-mini", label: "OpenRouter — GPT-4o mini" },
  { value: "openrouter-deepseek", label: "OpenRouter — DeepSeek" },
];

const on = (v, fallback) => (v == null ? fallback : !(v === false || v === 0 || v === "0"));

const EMPTY_ENTRY = { id: null, question: "", answer: "", tags: "", status: "active" };

function KnowledgeDialog({ entry, onClose, onSaved }) {
  const dlg = useDialogStyles();
  const [form, setForm] = useState(entry || EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(entry || EMPTY_ENTRY), [entry]);
  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.put(`/negocio/ai/knowledge/${form.id}`, form);
      else await api.post("/negocio/ai/knowledge", form);
      toast.success(tNegocio("toastSaved"));
      onSaved();
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!entry} onClose={onClose} maxWidth="sm" fullWidth classes={{ paper: dlg.paper }}>
      <DialogTitle>{form.id ? t("editEntry") : t("newEntry")}</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={t("question")}
          helperText={t("questionHint")}
          value={form.question}
          onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
        />
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          multiline
          minRows={5}
          label={t("answer")}
          value={form.answer}
          onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
        />
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={t("tags")}
          helperText={t("tagsHint")}
          value={form.tags || ""}
          onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
        />
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={t("status")}
          value={form.status}
          onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
        >
          <MenuItem value="active">{t("statusActive")}</MenuItem>
          <MenuItem value="pending">{t("statusPending")}</MenuItem>
          <MenuItem value="disabled">{t("statusDisabled")}</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tNegocio("close")}</Button>
        <Button color="primary" variant="contained" disabled={saving} onClick={save}>
          {tNegocio("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TestChat({ channel, setChannel }) {
  const classes = useStyles();
  const [log, setLog] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => endRef.current?.scrollIntoView({ block: "nearest" }), [log]);
  const send = async () => {
    const message = text.trim();
    if (!message || busy) return;
    const history = log.map((m) => ({ role: m.role, content: m.content }));
    setLog((l) => [...l, { role: "user", content: message }]);
    setText("");
    setBusy(true);
    try {
      const { data } = await api.post("/negocio/ai/test", { message, history, channel });
      setLog((l) => [...l, { role: "assistant", content: data.reply, tools: data.tools || [] }]);
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <TextField select size="small" variant="outlined" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <MenuItem value="whatsapp">WhatsApp</MenuItem>
          <MenuItem value="telegram">Telegram</MenuItem>
        </TextField>
        <Button size="small" onClick={() => setLog([])} disabled={!log.length}>
          {t("resetChat")}
        </Button>
      </div>
      <div
        style={{
          height: 320,
          overflowY: "auto",
          padding: 12,
          borderRadius: 8,
          border: "1px solid rgba(128,128,128,0.25)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {!log.length ? <div className={classes.captionBlock}>{t("testEmpty")}</div> : null}
        {log.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start",
              maxWidth: "85%",
              whiteSpace: "pre-wrap",
              padding: "8px 12px",
              borderRadius: 10,
              background: m.role === "user" ? "rgba(37,211,102,0.18)" : "rgba(128,128,128,0.12)",
            }}
          >
            {m.content}
            {m.tools?.length ? (
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>⚙️ {m.tools.join(", ")}</div>
            ) : null}
          </div>
        ))}
        {busy ? <CircularProgress size={18} style={{ alignSelf: "flex-start" }} /> : null}
        <div ref={endRef} />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t("testPlaceholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <IconButton color="primary" onClick={send} disabled={busy || !text.trim()}>
          <SendIcon />
        </IconButton>
      </div>
    </div>
  );
}

export default function AiAgentTab({ settings, setSettings, onSave }) {
  const classes = useStyles();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [learning, setLearning] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const load = useCallback(async () => {
    try {
      const [k, s] = await Promise.all([api.get("/negocio/ai/knowledge"), api.get("/negocio/ai/stats")]);
      setRows(k.data || []);
      setStats(s.data || null);
    } catch (e) {
      toastError(e);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const learn = async (kind) => {
    setLearning(kind);
    try {
      const { data } =
        kind === "history"
          ? await api.post("/negocio/ai/learn-history", { days: 30 })
          : await api.post("/negocio/ai/learn");
      toast.success(t("learnDone").replace("{reviewed}", data.reviewed).replace("{learned}", data.learned));
      load();
    } catch (e) {
      toastError(e);
    } finally {
      setLearning("");
    }
  };

  const setStatus = async (row, status) => {
    try {
      await api.put(`/negocio/ai/knowledge/${row.id}`, { ...row, status });
      load();
    } catch (e) {
      toastError(e);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/negocio/ai/knowledge/${row.id}`);
      load();
    } catch (e) {
      toastError(e);
    }
  };

  const visible = rows.filter((r) => filter === "all" || r.status === filter);
  const enabled = on(settings.aiEnabled, false);
  const card = { padding: 16, borderRadius: 10, border: "1px solid rgba(128,128,128,0.25)", marginBottom: 16 };

  return (
    <>
      <div style={card}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>{t("title")}</div>
        <div className={classes.captionBlock} style={{ marginBottom: 12 }}>
          {t("intro")}
        </div>
        {stats ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <Chip size="small" label={`${t("statActive")}: ${stats.active}`} />
            <Chip size="small" color={stats.pending ? "secondary" : "default"} label={`${t("statPending")}: ${stats.pending}`} />
            <Chip size="small" label={`${t("statLearned")}: ${stats.learned}`} />
            <Chip size="small" label={`${t("statEscalations")}: ${stats.escalations30d}`} />
          </div>
        ) : null}
        <FormControlLabel
          control={<Switch color="primary" checked={enabled} onChange={(e) => set({ aiEnabled: e.target.checked })} />}
          label={t("enabled")}
        />
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={on(settings.aiWhatsapp, true)}
              disabled={!enabled}
              onChange={(e) => set({ aiWhatsapp: e.target.checked })}
            />
          }
          label="WhatsApp"
        />
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={on(settings.aiTelegram, true)}
              disabled={!enabled}
              onChange={(e) => set({ aiTelegram: e.target.checked })}
            />
          }
          label="Telegram"
        />
        {enabled && !settings.aiApiKeySet && !String(settings.aiApiKey || "").trim() ? (
          <div className={classes.captionBlock} style={{ color: "#ed6c02", fontWeight: 700 }}>
            {t("needKey")}
          </div>
        ) : null}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 8 }}>
          <TextField
            select
            variant="outlined"
            margin="dense"
            label={t("provider")}
            value={settings.aiProvider || "openai-oficial-gpt4o-mini"}
            onChange={(e) => set({ aiProvider: e.target.value })}
          >
            {PROVIDERS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            variant="outlined"
            margin="dense"
            type="password"
            label={t("apiKey")}
            placeholder={settings.aiApiKeySet ? "********" : "sk-..."}
            value={settings.aiApiKey === "********" ? "" : settings.aiApiKey || ""}
            helperText={settings.aiApiKeySet ? t("apiKeySet") : t("apiKeyHint")}
            onChange={(e) => set({ aiApiKey: e.target.value })}
          />
          <TmdbToggle settings={settings} setSettings={setSettings} field="tmdbAiEnabled" label="Usar TMDB para recomendaciones del agente IA" />
          <p>Configura la clave compartida en Configuración → Integraciones.</p>
          <TextField
            variant="outlined"
            margin="dense"
            label={t("model")}
            helperText={t("modelHint")}
            value={settings.aiModel || ""}
            onChange={(e) => set({ aiModel: e.target.value })}
          />
          <TextField
            variant="outlined"
            margin="dense"
            label={t("agentName")}
            placeholder="StreamDigi"
            value={settings.aiAgentName || ""}
            onChange={(e) => set({ aiAgentName: e.target.value })}
          />
          <TextField
            variant="outlined"
            margin="dense"
            type="number"
            label={t("temperature")}
            helperText={t("temperatureHint")}
            inputProps={{ min: 0, max: 1, step: 0.1 }}
            value={settings.aiTemperature ?? 0.4}
            onChange={(e) => set({ aiTemperature: e.target.value })}
          />
          <TextField
            variant="outlined"
            margin="dense"
            type="number"
            label={t("history")}
            helperText={t("historyHint")}
            inputProps={{ min: 4, max: 40 }}
            value={settings.aiHistory ?? 16}
            onChange={(e) => set({ aiHistory: e.target.value })}
          />
        </div>
        <TextField
          fullWidth
          multiline
          minRows={7}
          variant="outlined"
          margin="dense"
          label={t("instructions")}
          placeholder={t("instructionsPlaceholder")}
          helperText={t("instructionsHint")}
          value={settings.aiInstructions || ""}
          onChange={(e) => set({ aiInstructions: e.target.value })}
        />
        <div style={{ marginTop: 8 }}>
          <FormControlLabel
            control={
              <Switch color="primary" checked={on(settings.aiAutoLearn, true)} onChange={(e) => set({ aiAutoLearn: e.target.checked })} />
            }
            label={t("autoLearn")}
          />
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={on(settings.aiLearnAutoApprove, false)}
                disabled={!on(settings.aiAutoLearn, true)}
                onChange={(e) => set({ aiLearnAutoApprove: e.target.checked })}
              />
            }
            label={t("autoApprove")}
          />
          <div className={classes.captionBlock}>{t("autoLearnHint")}</div>
          <FormControlLabel
            style={{ marginTop: 8 }}
            control={
              <Switch color="primary" checked={on(settings.aiVision, true)} onChange={(e) => set({ aiVision: e.target.checked })} />
            }
            label={t("vision")}
          />
          <div className={classes.captionBlock}>{t("visionHint")}</div>
          <FormControlLabel
            style={{ marginTop: 8 }}
            control={
              <Switch color="primary" checked={on(settings.aiAdminDigest, true)} onChange={(e) => set({ aiAdminDigest: e.target.checked })} />
            }
            label={t("adminDigest")}
          />
          <div className={classes.captionBlock}>{t("adminDigestHint")}</div>
        </div>
        <Button
          variant="contained"
          color="primary"
          style={{ marginTop: 12 }}
          onClick={async () => {
            await onSave();
            load();
          }}
        >
          {tNegocio("save")}
        </Button>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>{t("testTitle")}</div>
        <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
          {t("testHint")}
        </div>
        <TestChat channel={channel} setChannel={setChannel} />
      </div>

      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ fontWeight: 800, flex: 1 }}>{t("knowledgeTitle")}</div>
          <Button size="small" variant="outlined" onClick={() => setEditing({ ...EMPTY_ENTRY })}>
            {t("newEntry")}
          </Button>
          <Button size="small" variant="outlined" disabled={!!learning} onClick={() => learn("history")}>
            {learning === "history" ? <CircularProgress size={16} /> : t("learnHistory")}
          </Button>
          <Button size="small" variant="outlined" disabled={!!learning} onClick={() => learn("escalations")}>
            {learning === "escalations" ? <CircularProgress size={16} /> : t("learnNow")}
          </Button>
        </div>
        <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
          {t("knowledgeHint")}
        </div>
        <FilterChips
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: `${t("filterAll")} (${rows.length})` },
            { value: "pending", label: `${t("statusPending")} (${rows.filter((r) => r.status === "pending").length})` },
            { value: "active", label: t("statusActive") },
            { value: "disabled", label: t("statusDisabled") },
          ]}
        />
        <Table size="small" style={{ marginTop: 8 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t("question")}</TableCell>
              <TableCell>{t("answer")}</TableCell>
              <TableCell>{t("source")}</TableCell>
              <TableCell>{t("status")}</TableCell>
              <TableCell align="right">{t("uses")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!visible.length ? (
              <TableRow>
                <TableCell colSpan={6} className={classes.captionBlock}>
                  {t("knowledgeEmpty")}
                </TableCell>
              </TableRow>
            ) : null}
            {visible.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell style={{ maxWidth: 260, fontWeight: 600 }}>{r.question}</TableCell>
                <TableCell style={{ maxWidth: 380, whiteSpace: "pre-wrap" }}>
                  {String(r.answer || "").length > 220 ? `${r.answer.slice(0, 220)}…` : r.answer}
                </TableCell>
                <TableCell>{r.source === "learned" ? t("sourceLearned") : t("sourceManual")}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={t(r.status === "active" ? "statusActive" : r.status === "pending" ? "statusPending" : "statusDisabled")}
                    style={{
                      background: r.status === "active" ? "#2e7d32" : r.status === "pending" ? "#ed6c02" : "#757575",
                      color: "#fff",
                    }}
                  />
                </TableCell>
                <TableCell align="right">{r.hits || 0}</TableCell>
                <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                  {r.status === "pending" ? (
                    <IconButton size="small" title={t("approve")} onClick={() => setStatus(r, "active")}>
                      <CheckIcon fontSize="small" style={{ color: "#2e7d32" }} />
                    </IconButton>
                  ) : null}
                  <IconButton size="small" onClick={() => setEditing(r)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => remove(r)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <KnowledgeDialog
        entry={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </>
  );
}
